import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { languageApi } from "@/api/language.api";
import { leaderboardApi } from "@/api/leaderboard.api";
import { faqApi, isFaqNotModified } from "@/api/faq.api";
import { normalizeRows } from "@/components/leaderboard/leaderboard-ranking";
import type { HomeFaqItem } from "./public-home-data";

// The public data the home page reads besides /stats/platform (shared through
// usePlatformStats). All three routes answer without a session.

/** Active languages, for their local spelling. A failure only drops the names. */
export function useActiveLanguages() {
  return useQuery({
    queryKey: ["public-home", "langues", "active"],
    queryFn: () => languageApi.getActiveLanguages(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Top five of the week, all languages together. */
export function useWeeklyLeaders() {
  return useQuery({
    queryKey: ["public-home", "leaderboard", "week"],
    queryFn: async () => normalizeRows(await leaderboardApi.getGlobalLeaderboard("contributions", 5, "week"), 5),
    staleTime: 60_000,
    retry: 1,
  });
}

/** What the home page reads of a FAQ entry; anything malformed is dropped. */
const faqEntrySchema = z
  .object({
    id: z.number(),
    question: z.string().min(1).max(1000),
    answer: z.string().min(1).max(10000),
    orderIndex: z.number().finite().optional().default(0),
    isPublished: z.boolean().optional().default(true),
  })

/** Published FAQ entries, each one checked before it is rendered. */
export function usePublicFaq() {
  return useQuery({
    queryKey: ["public-home", "faq"],
    queryFn: async (): Promise<HomeFaqItem[]> => {
      const content = await faqApi.getFaq();
      if (isFaqNotModified(content) || !Array.isArray(content?.items)) return [];
      return content.items.flatMap((item) => {
        const parsed = faqEntrySchema.safeParse(item);
        return parsed.success ? [parsed.data as HomeFaqItem] : [];
      });
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

/** True when the reader asked the system for less motion. */
export function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * Counts from 0 up to `target` once, in about a second. Returns the target
 * straight away when motion is reduced, so the figure is never animated nor
 * shown as a misleading 0 to those readers.
 */
export function useCountUp(target: number, duration = 1100): number {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(reduced ? target : 0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (reduced || typeof requestAnimationFrame !== "function") {
      setValue(target);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [target, duration, reduced]);

  return value;
}
